/**
 * ContentOnlyLoadingFallback - Minimal Suspense fallback for nested account routes
 *
 * Simple loading state shown during nested route transitions INSIDE AccountManagementLayout.
 * Only shows spinner in content area without any layout structure (no sidebar placeholder).
 *
 * WHY THIS COMPONENT:
 * - Prevents double sidebar when loading nested account routes
 * - AccountManagementLayout already provides the sidebar structure
 * - Nested routes should only show loading in the content area
 * - Eliminates "two vertical blocks of differing colours" issue
 *
 * WHY separate from AccountLoadingFallback:
 * - AccountLoadingFallback: Full layout with sidebar placeholder (for parent /account route)
 * - ContentOnlyLoadingFallback: Just content area (for nested routes inside layout)
 * - Prevents layout duplication and visual glitches
 *
 * WHY minimal structure:
 * - No sidebar placeholder (parent layout already has real sidebar)
 * - Just a centered spinner in content area
 * - Matches the <Outlet /> content area in AccountManagementLayout
 * - Prevents jarring loading state transitions
 *
 * USAGE:
 * Used as fallback prop in React Suspense for NESTED account routes in App.jsx:
 * <Suspense fallback={<ContentOnlyLoadingFallback />}>
 *   <ProfileEdit />
 * </Suspense>
 *
 * Shows during:
 * - Navigation between account pages (Profile → Edit, etc.)
 * - Lazy loading of nested account components
 * - Any route transitions inside AccountManagementLayout
 *
 * DO NOT USE for:
 * - Parent /account route (use AccountLoadingFallback)
 * - Routes outside account management (use GeneralLoadingFallback)
 *
 * @see App.jsx - Suspense boundary implementation for nested routes
 * @see src/features/account/AccountManagementLayout.jsx - Parent layout with sidebar
 * @see src/ui/AccountLoadingFallback.tsx - Full layout variant for parent route
 * @see src/ui/Spinner.tsx - Base spinner component
 */

import styled from 'styled-components';
import Spinner from './Spinner';

/**
 * Content area container
 *
 * WHY minimal styling:
 * - Parent layout (AccountManagementLayout) provides structure
 * - Just needs to fill space and center spinner
 * - No background color needed (inherits from layout)
 *
 * WHY calc() for min-height:
 * - Mobile fallback: fills viewport height minus FormContainer padding
 * - Mobile: 100vh - (24px top + 24px bottom) = calc(100vh - 48px)
 * - Desktop overrides this with position: fixed (full viewport centering)
 * - The base min-height (128px offset) is kept as a safe fallback
 *
 * WHY no additional padding:
 * - Parent FormContainer already provides spacing (64px desktop, 24px mobile)
 * - Adding more padding would push content off-center
 * - Width: 100% ensures full horizontal centering
 *
 * WHY fadeIn animation:
 * - Smooth transition when fallback appears
 * - 0.15s is fast enough to feel instant
 * - Reduces perceived loading time
 * - Matches AccountLoadingFallback animation timing
 */
const ContentArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* WHY calc(): Mobile fallback for viewport height minus FormContainer padding.
   * Desktop overrides with position: fixed below, so this min-height only matters on mobile. */
  min-height: calc(100vh - 128px);
  animation: fadeIn 0.15s ease-in;

  /* WHY fixed positioning on desktop:
   * The spinner renders inside the 1fr grid column (after the 480px sidebar),
   * so flexbox centering places it in the middle of the content panel — not the viewport.
   * This makes the spinner appear shifted right on desktop.
   * Using position: fixed breaks out of the grid flow and centers on the full viewport.
   * pointer-events: none keeps the sidebar clickable underneath.
   *
   * TODO: A cleaner long-term fix would be rendering this fallback via a React Portal
   * at the body level, or restructuring the Suspense boundary to sit outside the grid.
   * For now, fixed positioning is the least-invasive fix. */
  @media screen and (min-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 10;
    pointer-events: none;
  }

  /* WHY mobile stays as-is: No sidebar on mobile, so panel center = viewport center.
   * FormContainer has 24px padding on mobile: 100vh - (24px top + 24px bottom) */
  @media screen and (max-width: 767px) {
    min-height: calc(100vh - 48px);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

/**
 * ContentOnlyLoadingFallback component
 *
 * WHY function component:
 * - No props needed (Suspense fallback is stateless)
 * - Matches pattern of other loading fallback components
 * - Simplicity is key for fallback UI
 * - Easy to test and maintain
 */
function ContentOnlyLoadingFallback(): JSX.Element {
  return (
    <ContentArea>
      <Spinner />
    </ContentArea>
  );
}

export default ContentOnlyLoadingFallback;
